import React, { useMemo } from 'react';
import { Activity, useRecentActivity } from '../useData';
import { getDateRangeFromTimeRange } from '../contexts/DashboardFiltersContext';
import type { TimeRange } from './TimeRangeSelector';

export interface RecentActivityProps {
  activities?: Activity[];
  isLoading?: boolean;
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  timeRange?: TimeRange;
}

const RecentActivity: React.FC<RecentActivityProps> = ({
  activities: activitiesProp,
  isLoading: isLoadingProp,
  limit = 10,
  embedded,
  settings,
  isEditing,
  timeRange = 'month',
}) => {
  const effectiveLimit = typeof (settings?.limit ?? limit) === 'number' ? (settings?.limit as number) : limit;
  const { activities: fetchedActivities, isLoading: isLoadingFetch } = useRecentActivity(embedded ? effectiveLimit : 10);
  const { startDate, endDate } = useMemo(() => getDateRangeFromTimeRange(timeRange), [timeRange]);
  const rawActivities = activitiesProp ?? (embedded ? fetchedActivities : []);
  const activities = useMemo(() => {
    if (timeRange === 'all') return rawActivities;
    return rawActivities.filter((a) => {
      const created = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      return created >= startDate.getTime() && created <= endDate.getTime();
    });
  }, [rawActivities, timeRange, startDate, endDate]);
  const isLoading = isLoadingProp ?? (embedded ? isLoadingFetch : false);

  return (
    <div className={`rounded-xl shadow-sm p-6 border border-gray-100 ${embedded ? 'min-h-0 flex flex-col' : 'bg-white'}`}>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="flex items-center space-x-4 pb-4 border-b border-gray-100 last:border-0">
                <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 animate-pulse rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : activities.length > 0 ? (
          activities.map((activity, index) => (
            <div key={activity.id || index} className="flex items-center space-x-4 pb-4 border-b border-gray-100 last:border-0">
              <div className="h-10 w-10 rounded-full bg-teal-50 text-teal-700 ring-1 ring-teal-200/70 dark:bg-teal-900/30 dark:text-teal-300 dark:ring-teal-800/50 flex items-center justify-center">
                <span className="text-white text-xs font-semibold">
                  {activity.userName?.[0]?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{activity.message || activity.description}</p>
                <p className="text-xs text-gray-500">
                  {activity.createdAt ? new Date(activity.createdAt).toLocaleString() : 'Recently'}
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No recent activity</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivity;

